import { ArrowPathIcon } from "@heroicons/react/24/solid";
import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { ROUTES } from "../routes";
import { LoginPageLayout } from "../routes/LoginPageLayout";
import { UserContext, useAuth } from "./hooks";

export {
    UserContext,
    type UserState,
    useAuth,
    useHasFlag,
    useLogout,
    usePermission,
    useUser,
} from "./hooks";

/**
 * Shown while we're waiting for the login check.
 */
export const CheckingCredentials = () => (
    <>
        <h2 className="mt-12 text-center text-2xl font-bold leading-9 tracking-tight text-gray-900">
            Checking your credentials...
        </h2>
        <p className="flex mt-5 justify-center">
            <ArrowPathIcon className="w-14 h-14 animate-spin" />
        </p>
    </>
);

/** The same thing, wrapped in the login layout, for use outside the login route. */
export const CheckingCredentialsPage = () => (
    <LoginPageLayout>
        <CheckingCredentials />
    </LoginPageLayout>
);

/**
 * Gates the authenticated part of the app: nothing below it renders until the
 * session check resolves.
 *
 * This also avoids any issues with the user/flags/hooks being undefined.
 *
 * Anyone without a session is sent to the login page, with the page they were
 * after in ?next= so they land back on it once they are through.
 */
export const AuthProvider = ({ children }: { children: ReactNode }) => {
    const { userState, loading } = useAuth();
    const location = useLocation();

    if (loading) {
        return <CheckingCredentialsPage />;
    }

    if (!userState) {
        const params = new URLSearchParams();
        if (location.pathname !== ROUTES.ROOT) {
            params.set("next", `${location.pathname}${location.search}`);
        }
        const query = params.toString();
        return (
            <Navigate
                to={query ? `${ROUTES.LOGIN}?${query}` : ROUTES.LOGIN}
                replace
            />
        );
    }

    return (
        <UserContext.Provider value={userState}>
            {children}
        </UserContext.Provider>
    );
};
